/* eslint-disable babel/camelcase */
import { enumValue } from './enums';

export interface ReferralType {
    id: number;
    referral_message: {
        base36Id: string;
        reason: string;
        type: number;
        viewed: number;
        to_name: string;
        to_organization_name: string;
        location?: {
            id: number;
            name: string;
        };
    };
    sent_on: string;
    created: string;
    updated: string;
    status: number;
    custom_status: null | {
        name: string;
        status: number;
        expose: number;
    };
    unread_notes_count: number;
    failed_transmissions_count: number;
    children?: {
        type: number;
        appointment?: {
            appointment_date: string;
            time_pending: boolean;
            status: number;
        };
    }[];
}

export const referralStatusNames = {
    [enumValue(['core_app', 'referral_statuses', 'Draft']) as number]: 'Draft',
    [enumValue(['core_app', 'referral_statuses', 'Created']) as number]: 'Created',
    [enumValue(['core_app', 'referral_statuses', 'Sent']) as number]: 'New',
    [enumValue(['core_app', 'referral_statuses', 'Received']) as number]: 'Received',
    [enumValue(['core_app', 'referral_statuses', 'Acknowledged']) as number]: 'Acknowledged',
    [enumValue(['core_app', 'referral_statuses', 'Accepted']) as number]: 'Accepted',
    [enumValue(['core_app', 'referral_statuses', 'Scheduled']) as number]: 'Scheduled',
    [enumValue(['core_app', 'referral_statuses', 'Completed']) as number]: 'Completed',
    [enumValue(['core_app', 'referral_statuses', 'Stale']) as number]: 'Stale Referral',
    [enumValue(['core_app', 'referral_statuses', 'Cancelled']) as number]: 'Cancelled',
    [enumValue(['core_app', 'referral_statuses', 'Declined']) as number]: 'Declined',
};

export function getReferralStatusName(referral: ReferralType) {
    if (
        referral.status === enumValue(['core_app', 'referral_statuses', 'Created']) ||
        referral.status === enumValue(['core_app', 'referral_statuses', 'Sent'])
    ) {
        if (referral.referral_message.type === enumValue(['core_app', 'referral_types', 'Fax'])) {
            if (referral.referral_message.viewed) return 'Viewed';
            if (referral.status === enumValue(['core_app', 'referral_statuses', 'Sent']))
                return 'Delivered';
            return 'Sent';
        }
        return referral.referral_message?.viewed ? 'Viewed' : 'Pending';
    }
    return referralStatusNames[referral.status];
}

export const getReferralStatusColorClass = (referral: ReferralType) => {
    const status = referral.custom_status?.status || referral.status;
    switch (status) {
        case enumValue(['core_app', 'referral_statuses', 'Sent']):
        case enumValue(['core_app', 'referral_statuses', 'Open']):
            return 'bg-primary';
        case enumValue(['core_app', 'referral_statuses', 'Accepted']):
            return 'bg-success';
        case enumValue(['core_app', 'referral_statuses', 'Declined']):
        case enumValue(['core_app', 'referral_statuses', 'Closed']):
            return 'bg-danger';
        case enumValue(['core_app', 'referral_statuses', 'Cancelled']):
            return 'badge-outline-danger';
        case enumValue(['core_app', 'referral_statuses', 'Scheduled']):
            return 'bg-info';
        case enumValue(['core_app', 'referral_statuses', 'Received']):
            return 'badge-outline-info';
        case enumValue(['core_app', 'referral_statuses', 'Completed']):
        case enumValue(['core_app', 'referral_statuses', 'Draft']):
            return 'bg-dark';
        default:
            return 'bg-primary';
    }
};

// return the nearest future appointment if available,
// else return nearest past appointment available, else return undefined
export function getReferralAppointment(referral: ReferralType) {
    const currentDate = new Date();
    const currentUTC = new Date(
        Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())
    ).getTime();
    // children with type = 3 are appointments
    const ref = referral.children
        ?.filter(
            (x) => x.type === 3 && x.appointment?.status === 1 && x.appointment?.appointment_date
        )
        .sort((a, b) => {
            const aDate = new Date(a.appointment!.appointment_date).getTime();
            const bDate = new Date(b.appointment!.appointment_date).getTime();
            if (aDate >= currentUTC && bDate < currentUTC) return -1;
            if (aDate < currentUTC && bDate >= currentUTC) return 1;
            if (aDate < currentUTC && bDate < currentUTC) return bDate - aDate;
            return aDate - bDate;
        });
    return ref?.[0]?.appointment;
}
